import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type CSSProperties } from 'react';

import {
  Button,
  Card,
  IconArrowsMove,
  IconEraser,
  IconMagnifyingGlass,
  IconPaintBrush,
  IconPaw,
  IconSquare,
  IconUsers,
  Input,
  Spinner,
} from '@/components';
import { AnimalPanel } from '@/components/village-map/AnimalPanel';
import { FloatingToolCursor } from '@/components/village-map/FloatingToolCursor';
import { FurnitureWordPanel } from '@/components/village-map/FurnitureWordPanel';
import { InspectorSection } from '@/components/village-map/InspectorSection';
import {
  MapGrid,
  type MapTool,
  type MoveSelection,
} from '@/components/village-map/MapGrid';
import { MapPalette } from '@/components/village-map/MapPalette';
import { NpcPanel } from '@/components/village-map/NpcPanel';
import { LockMessagePanel } from '@/components/village-map/LockMessagePanel';
import { SignPanel } from '@/components/village-map/SignPanel';
import { TipPanel } from '@/components/village-map/TipPanel';
import { WarpPanel } from '@/components/village-map/WarpPanel';
import type {
  BuildingInteriorId,
  InteriorAreaDoc,
  InteriorKit,
  OutdoorAreaId,
  VillageMapAnimal,
  VillageMapBuilding,
  VillageMapDocument,
  VillageMapEdgeWarp,
  VillageMapLegend,
  VillageMapNpc,
  VillageMapRecord,
  VillageMapSign,
} from '@/lib/admin-types';
import { apiJson } from '@/lib/api';
import { formatAdminDateTime } from '@/lib/format';
import {
  AREA_DIMENSIONS,
  AREA_TAB_LABEL,
  BRUSH_SIZES,
  BUILDING_INTERIOR_IDS,
  cloneAreaRows,
  cloneDocument,
  formatInteriorHoverReadout,
  formatMapHoverReadout,
  INTERIOR_HEIGHT,
  INTERIOR_KITS,
  INTERIOR_TAB_LABEL,
  interiorFloorKitOf,
  interiorRugKitOf,
  INTERIOR_WIDTH,
  interiorToGridArea,
  isBrushSize,
  isBuildingInteriorId,
  LOCAL_INTERIOR_LEGEND,
  LOCAL_LEGEND,
  LOCAL_LEGEND_MAP,
  OUTDOOR_ANIMAL_BY_ID,
  OUTDOOR_AREA_IDS,
  OUTDOOR_NPC_BY_ID,
  addAnimalOnDocument,
  collectAnimalInstanceIds,
  makeAnimalInstanceId,
  moveAnimalOnDocument,
  parseVillageMapSaveError,
  applyMoveGroup,
  moveFurnitureWordsForGroup,
  moveGroupFits,
  pickMovableGroup,
  placeNpcOnDocument,
  placeResidentOnInterior,
  pruneFurnitureWords,
  removeAnimalById,
  removeNpcById,
  removeResidentFromInterior,
  removeSignAt,
  setCellChar,
  setFurnitureWordOnInterior,
  tipKey,
  upsertSignAt,
  validateVillageMapDocument,
  type BrushSize,
} from '@/lib/village-map';
import {
  MAP_STAMP_BY_ID,
  buildingFromStamp,
  pruneBuildingsAfterPaint,
  stampWriteCells,
  upsertBuildingPlacement,
  type MapStamp,
  type PlacedBuilding,
} from '@/lib/village-map-stamps';

export const Route = createFileRoute('/admin/village-maps/$id')({
  component: VillageMapDetailPage,
});

type EditorTab =
  | { mode: 'outdoor'; areaId: OutdoorAreaId }
  | { mode: 'interior'; interiorId: BuildingInteriorId };

type OutdoorUndoEntry = {
  kind: 'outdoor';
  areaId: OutdoorAreaId;
  rows: string[];
  buildings?: VillageMapBuilding[];
  signs?: VillageMapSign[];
  /** Full-document NPC snapshot (placement can move across areas). */
  npcsByArea: Record<OutdoorAreaId, VillageMapNpc[]>;
  /** Full-document animal snapshot (placement can move across areas). */
  animalsByArea: Record<OutdoorAreaId, VillageMapAnimal[]>;
};

type InteriorUndoEntry = {
  kind: 'interior';
  interiorId: BuildingInteriorId;
  room: InteriorAreaDoc;
};

type UndoEntry = OutdoorUndoEntry | InteriorUndoEntry;

function cloneBuildings(
  buildings: VillageMapBuilding[] | undefined,
): VillageMapBuilding[] | undefined {
  if (buildings == null) return undefined;
  return structuredClone(buildings);
}

function cloneSigns(
  signs: VillageMapSign[] | undefined,
): VillageMapSign[] | undefined {
  if (signs == null) return undefined;
  return structuredClone(signs);
}

function cloneNpcsByArea(
  doc: VillageMapDocument,
): Record<OutdoorAreaId, VillageMapNpc[]> {
  const out = {} as Record<OutdoorAreaId, VillageMapNpc[]>;
  for (const id of OUTDOOR_AREA_IDS) {
    out[id] = structuredClone(doc.outdoor[id]?.npcs ?? []);
  }
  return out;
}

function applyNpcsByArea(
  doc: VillageMapDocument,
  npcsByArea: Record<OutdoorAreaId, VillageMapNpc[]>,
): void {
  for (const id of OUTDOOR_AREA_IDS) {
    doc.outdoor[id].npcs = structuredClone(npcsByArea[id] ?? []);
  }
}

function cloneAnimalsByArea(
  doc: VillageMapDocument,
): Record<OutdoorAreaId, VillageMapAnimal[]> {
  const out = {} as Record<OutdoorAreaId, VillageMapAnimal[]>;
  for (const id of OUTDOOR_AREA_IDS) {
    out[id] = structuredClone(doc.outdoor[id]?.animals ?? []);
  }
  return out;
}

function applyAnimalsByArea(
  doc: VillageMapDocument,
  animalsByArea: Record<OutdoorAreaId, VillageMapAnimal[]>,
): void {
  for (const id of OUTDOOR_AREA_IDS) {
    doc.outdoor[id].animals = structuredClone(animalsByArea[id] ?? []);
  }
}

const ZOOM_OPTIONS = [8, 10, 12, 14, 16, 20, 24, 28, 32, 40, 48] as const;
type ZoomLevel = (typeof ZOOM_OPTIONS)[number];

const PALETTE_COLLAPSED_KEY = 'admin.village-maps.paletteCollapsed';
const INSPECTOR_COLLAPSED_KEY = 'admin.village-maps.inspectorCollapsed';
const BRUSH_SIZE_KEY = 'admin.village-maps.brushSize';
const AUTO_SAVE_KEY = 'admin.village-maps.autoSave';
/** Debounce paint/edit bursts so auto-save feels continuous without spamming PATCH. */
const AUTO_SAVE_DEBOUNCE_MS = 400;

function readCollapsedPref(key: string): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === '1' || v === 'true';
  } catch {
    return false;
  }
}

function writeCollapsedPref(key: string, value: boolean) {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // ignore quota / private mode
  }
}

function readAutoSavePref(): boolean {
  try {
    const v = localStorage.getItem(AUTO_SAVE_KEY);
    if (v === null) return true; // default ON
    return v === '1' || v === 'true';
  } catch {
    return true;
  }
}

function writeAutoSavePref(value: boolean) {
  writeCollapsedPref(AUTO_SAVE_KEY, value);
}

function readBrushSizePref(): BrushSize {
  try {
    const n = Number(localStorage.getItem(BRUSH_SIZE_KEY));
    if (isBrushSize(n)) return n;
  } catch {
    // ignore
  }
  return 1;
}

function writeBrushSizePref(size: BrushSize) {
  try {
    localStorage.setItem(BRUSH_SIZE_KEY, String(size));
  } catch {
    // ignore quota / private mode
  }
}

function stepZoom(current: ZoomLevel, delta: -1 | 1): ZoomLevel {
  const idx = ZOOM_OPTIONS.indexOf(current);
  const next = Math.min(
    ZOOM_OPTIONS.length - 1,
    Math.max(0, (idx < 0 ? 0 : idx) + delta),
  );
  return ZOOM_OPTIONS[next];
}

/** Shared chrome for palette / inspector collapse toggles (~44px hit target). */
function PanelCollapseButton({
  direction,
  label,
  expanded,
  onClick,
}: {
  direction: 'left' | 'right';
  label: string;
  expanded: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-300 bg-white text-gray-700 shadow-sm hover:border-primary/50 hover:bg-primary/10 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
      aria-label={label}
      aria-expanded={expanded}
      title={label}
      onClick={onClick}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        className="h-5 w-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {direction === 'left' ? (
          <path d="M12.5 4.5 7 10l5.5 5.5" />
        ) : (
          <path d="M7.5 4.5 13 10l-5.5 5.5" />
        )}
      </svg>
    </button>
  );
}

function VillageMapDetailPage() {
  const { id } = Route.useParams();
  const mapId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Survives editor remounts so Save cannot reset zoom to the default.
  // Outdoor / interior remember separately (interiors default to native atlas size).
  const [outdoorCellSize, setOutdoorCellSize] = useState<ZoomLevel>(28);
  const [interiorCellSize, setInteriorCellSize] = useState<ZoomLevel>(48);

  const detailQ = useQuery({
    queryKey: ['admin', 'village-map', mapId],
    queryFn: () => apiJson<VillageMapRecord>(`/v1/admin/village-maps/${mapId}`),
    enabled: Number.isFinite(mapId),
  });

  const legendQ = useQuery({
    queryKey: ['admin', 'village-maps', 'legend'],
    queryFn: () => apiJson<VillageMapLegend>('/v1/admin/village-maps/legend'),
    staleTime: 60_000,
  });

  // Once loaded, keep showing the editor during background refetches.
  if (detailQ.isLoading && !detailQ.data) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if ((detailQ.isError && !detailQ.data) || !detailQ.data) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-red-600">
          {(detailQ.error as Error)?.message ?? 'Not found'}
        </p>
        <Button
          variant="secondary"
          onClick={() => void navigate({ to: '/admin/village-maps' })}
        >
          ← All village maps
        </Button>
      </div>
    );
  }

  return (
    <VillageMapEditor
      key={detailQ.data.id}
      record={detailQ.data}
      legend={legendQ.data ?? LOCAL_LEGEND_MAP}
      outdoorCellSize={outdoorCellSize}
      interiorCellSize={interiorCellSize}
      onOutdoorCellSizeChange={setOutdoorCellSize}
      onInteriorCellSizeChange={setInteriorCellSize}
      onBack={() => void navigate({ to: '/admin/village-maps' })}
      onSaved={(next) => {
        qc.setQueryData(['admin', 'village-map', mapId], next);
        void qc.invalidateQueries({
          queryKey: ['admin', 'village-maps'],
          exact: true,
        });
      }}
    />
  );
}

function VillageMapEditor({
  record,
  legend,
  outdoorCellSize,
  interiorCellSize,
  onOutdoorCellSizeChange,
  onInteriorCellSizeChange,
  onBack,
  onSaved,
}: {
  record: VillageMapRecord;
  legend: VillageMapLegend;
  outdoorCellSize: ZoomLevel;
  interiorCellSize: ZoomLevel;
  onOutdoorCellSizeChange: (next: ZoomLevel) => void;
  onInteriorCellSizeChange: (next: ZoomLevel) => void;
  onBack: () => void;
  onSaved: (next: VillageMapRecord) => void;
}) {
  const [name, setName] = useState(record.name);
  const [notes, setNotes] = useState(record.notes ?? '');
  const [document, setDocument] = useState<VillageMapDocument>(() =>
    cloneDocument(record.document),
  );
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState(record.updated_at);
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState<EditorTab>({ mode: 'outdoor', areaId: 'town' });
  const [tool, setTool] = useState<MapTool>('paint');
  const [moveSelection, setMoveSelection] = useState<MoveSelection | null>(
    null,
  );
  const [selectedChar, setSelectedChar] = useState('.');
  const [selectedStampId, setSelectedStampId] = useState<string | null>(null);
  const [selectedNpcId, setSelectedNpcId] = useState<string | null>(null);
  /** Catalog kind — Place Animal adds a new instance of this kind. */
  const [selectedAnimalKind, setSelectedAnimalKind] = useState<string | null>(
    null,
  );
  /** Instance id — Place Animal moves this instance. */
  const [selectedAnimalInstanceId, setSelectedAnimalInstanceId] = useState<
    string | null
  >(null);
  /** Bumped to trigger MapGrid’s short attention flash on the selected entity. */
  const [focusPulseKey, setFocusPulseKey] = useState(0);
  const [brushSize, setBrushSize] = useState<BrushSize>(() => readBrushSizePref());
  const [showSprites, setShowSprites] = useState(true);
  const [autoSave, setAutoSave] = useState(() => readAutoSavePref());
  const [selectedCell, setSelectedCell] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [hoverCell, setHoverCell] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [highlightCell, setHighlightCell] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const pulseEntityFocus = () => setFocusPulseKey((k) => k + 1);
  const [clientErrors, setClientErrors] = useState<string[]>([]);
  const [serverWarnings, setServerWarnings] = useState<string[]>(
    record.warnings ?? [],
  );
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [paletteCollapsed, setPaletteCollapsed] = useState(() =>
    readCollapsedPref(PALETTE_COLLAPSED_KEY),
  );
  const [inspectorCollapsed, setInspectorCollapsed] = useState(() =>
    readCollapsedPref(INSPECTOR_COLLAPSED_KEY),
  );

  const undoStack = useRef<UndoEntry[]>([]);
  const redoStack = useRef<UndoEntry[]>([]);
  const documentRef = useRef(document);
  documentRef.current = document;
  const nameRef = useRef(name);
  nameRef.current = name;
  const notesRef = useRef(notes);
  notesRef.current = notes;
  const expectedUpdatedAtRef = useRef(expectedUpdatedAt);
  expectedUpdatedAtRef.current = expectedUpdatedAt;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const autoSaveRef = useRef(autoSave);
  autoSaveRef.current = autoSave;
  /** Bumped on every local edit; compared after save to avoid clobbering newer work. */
  const editEpochRef = useRef(0);
  const savingRef = useRef(false);
  const pendingAutoSaveRef = useRef(false);
  const clearUndoOnSuccessRef = useRef(false);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    writeCollapsedPref(PALETTE_COLLAPSED_KEY, paletteCollapsed);
  }, [paletteCollapsed]);

  useEffect(() => {
    writeCollapsedPref(INSPECTOR_COLLAPSED_KEY, inspectorCollapsed);
  }, [inspectorCollapsed]);

  useEffect(() => {
    writeBrushSizePref(brushSize);
  }, [brushSize]);

  useEffect(() => {
    writeAutoSavePref(autoSave);
  }, [autoSave]);

  const syncUndoButtons = () => {
    setCanUndo(undoStack.current.length > 0);
    setCanRedo(redoStack.current.length > 0);
  };

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const markDirty = () => {
    editEpochRef.current += 1;
    setDirty(true);
  };

  const pushUndo = (
    area: OutdoorAreaId,
    rows: string[],
    buildings?: VillageMapBuilding[],
    signs?: VillageMapSign[],
    npcsByArea?: Record<OutdoorAreaId, VillageMapNpc[]>,
    animalsByArea?: Record<OutdoorAreaId, VillageMapAnimal[]>,
  ) => {
    undoStack.current.push({
      kind: 'outdoor',
      areaId: area,
      rows,
      buildings: cloneBuildings(buildings) ?? [],
      signs: cloneSigns(signs) ?? [],
      npcsByArea: npcsByArea ?? cloneNpcsByArea(documentRef.current),
      animalsByArea: animalsByArea ?? cloneAnimalsByArea(documentRef.current),
    });
    if (undoStack.current.length > 100) undoStack.current.shift();
    redoStack.current = [];
    syncUndoButtons();
  };

  const pushInteriorUndo = (interiorId: BuildingInteriorId, room: InteriorAreaDoc) => {
    undoStack.current.push({
      kind: 'interior',
      interiorId,
      room: structuredClone(room),
    });
    if (undoStack.current.length > 100) undoStack.current.shift();
    redoStack.current = [];
    syncUndoButtons();
  };

  const switchToOutdoor = (areaId: OutdoorAreaId) => {
    setTab({ mode: 'outdoor', areaId });
    setSelectedCell(null);
    setHoverCell(null);
    setHighlightCell(null);
    setSelectedStampId(null);
    setMoveSelection(null);
    if (tool === 'stamp' || tool === 'animal') setTool('paint');
  };

  const switchToInterior = (interiorId: BuildingInteriorId) => {
    setTab({ mode: 'interior', interiorId });
    setSelectedCell(null);
    setHoverCell(null);
    setHighlightCell(null);
    setSelectedStampId(null);
    setSelectedAnimalKind(null);
    setSelectedAnimalInstanceId(null);
    setMoveSelection(null);
    setSelectedChar('=');
    if (tool === 'stamp' || tool === 'animal') setTool('paint');
  };

  const updateAreaTips = (
    target: OutdoorAreaId,
    tips: Record<string, string>,
  ) => {
    setDocument((prev) => {
      const next = cloneDocument(prev);
      next.outdoor[target].tips = tips;
      return next;
    });
    markDirty();
  };

  const updateAreaLockMessages = (
    target: OutdoorAreaId,
    lockMessages: Record<string, string>,
  ) => {
    setDocument((prev) => {
      const next = cloneDocument(prev);
      next.outdoor[target].lockMessages = lockMessages;
      return next;
    });
    markDirty();
  };

  const updateAreaSigns = (target: OutdoorAreaId, signs: VillageMapSign[]) => {
    setDocument((prev) => {
      const next = cloneDocument(prev);
      next.outdoor[target].signs = signs;
      return next;
    });
    markDirty();
  };

  const placeNpc = (npcId: string, x: number, y: number) => {
    const prev = documentRef.current;
    if (tab.mode === 'interior') {
      const room = prev.interiors[tab.interiorId];
      const existing = (room?.residents ?? []).find((r) => r.npcId === npcId);
      if (existing && existing.x === x && existing.y === y) {
        setSelectedCell({ x, y });
        return;
      }
      if (room) pushInteriorUndo(tab.interiorId, room);
      setDocument((doc) =>
        placeResidentOnInterior(doc, tab.interiorId, npcId, x, y),
      );
      setSelectedCell({ x, y });
      markDirty();
      return;
    }
    const areaId = tab.areaId;
    const area = prev.outdoor[areaId];
    const existing = (area.npcs ?? []).find((n) => n.id === npcId);
    if (existing && existing.x === x && existing.y === y) {
      setSelectedCell({ x, y });
      return;
    }
    pushUndo(
      areaId,
      cloneAreaRows(area),
      area.buildings,
      area.signs,
      cloneNpcsByArea(prev),
      cloneAnimalsByArea(prev),
    );
    setDocument((doc) => placeNpcOnDocument(doc, areaId, npcId, x, y));
    setSelectedCell({ x, y });
    markDirty();
  };

  const removeNpc = (npcId: string) => {
    const prev = documentRef.current;
    if (tab.mode === 'interior') {
      const room = prev.interiors[tab.interiorId];
      if (!(room?.residents ?? []).some((r) => r.npcId === npcId)) return;
      if (room) pushInteriorUndo(tab.interiorId, room);
      setDocument((doc) =>
        removeResidentFromInterior(doc, tab.interiorId, npcId),
      );
      markDirty();
      return;
    }
    const areaId = tab.areaId;
    const area = prev.outdoor[areaId];
    if (!(area.npcs ?? []).some((n) => n.id === npcId)) return;
    pushUndo(
      areaId,
      cloneAreaRows(area),
      area.buildings,
      area.signs,
      cloneNpcsByArea(prev),
      cloneAnimalsByArea(prev),
    );
    setDocument((doc) => {
      const next = cloneDocument(doc);
      for (const id of OUTDOOR_AREA_IDS) {
        next.outdoor[id].npcs = removeNpcById(next.outdoor[id].npcs, npcId);
      }
      return next;
    });
    markDirty();
  };

  const updateNpcNote = (npcId: string, note: string) => {
    setDocument((prev) => {
      const next = cloneDocument(prev);
      for (const id of OUTDOOR_AREA_IDS) {
        const list = next.outdoor[id].npcs ?? [];
        const idx = list.findIndex((n) => n.id === npcId);
        if (idx >= 0) {
          const cur = list[idx]!;
          list[idx] = {
            ...cur,
            note: note === '' ? undefined : note,
          };
          next.outdoor[id].npcs = list;
          break;
        }
      }
      return next;
    });
    markDirty();
  };

  const placeAnimal = (x: number, y: number) => {
    if (tab.mode !== 'outdoor') return;
    const areaId = tab.areaId;
    const prev = documentRef.current;
    const area = prev.outdoor[areaId];
    if (selectedAnimalInstanceId) {
      const existing = OUTDOOR_AREA_IDS.flatMap(
        (id) => prev.outdoor[id]?.animals ?? [],
      ).find((a) => a.id === selectedAnimalInstanceId);
      if (
        existing &&
        existing.x === x &&
        existing.y === y &&
        (area.animals ?? []).some((a) => a.id === selectedAnimalInstanceId)
      ) {
        setSelectedCell({ x, y });
        return;
      }
      pushUndo(
        areaId,
        cloneAreaRows(area),
        area.buildings,
        area.signs,
        cloneNpcsByArea(prev),
        cloneAnimalsByArea(prev),
      );
      setDocument((doc) =>
        moveAnimalOnDocument(doc, areaId, selectedAnimalInstanceId, x, y),
      );
      setSelectedCell({ x, y });
      markDirty();
      return;
    }
    if (!selectedAnimalKind) return;
    const instanceId = makeAnimalInstanceId(
      selectedAnimalKind,
      collectAnimalInstanceIds(prev),
    );
    pushUndo(
      areaId,
      cloneAreaRows(area),
      area.buildings,
      area.signs,
      cloneNpcsByArea(prev),
      cloneAnimalsByArea(prev),
    );
    setDocument((doc) =>
      addAnimalOnDocument(
        doc,
        areaId,
        selectedAnimalKind,
        x,
        y,
        instanceId,
      ),
    );
    // Select the new instance so a follow-up click moves it instead of
    // stacking another pin on the same cell.
    setSelectedAnimalInstanceId(instanceId);
    setSelectedAnimalKind(null);
    setSelectedCell({ x, y });
    markDirty();
  };

  const removeAnimal = (animalId: string) => {
    if (tab.mode !== 'outdoor') return;
    const areaId = tab.areaId;
    const prev = documentRef.current;
    const area = prev.outdoor[areaId];
    if (!(area.animals ?? []).some((a) => a.id === animalId)) return;
    pushUndo(
      areaId,
      cloneAreaRows(area),
      area.buildings,
      area.signs,
      cloneNpcsByArea(prev),
      cloneAnimalsByArea(prev),
    );
    setDocument((doc) => {
      const next = cloneDocument(doc);
      for (const id of OUTDOOR_AREA_IDS) {
        next.outdoor[id].animals = removeAnimalById(
          next.outdoor[id].animals,
          animalId,
        );
      }
      return next;
    });
    markDirty();
  };

  const updateAnimalNote = (animalId: string, note: string) => {
    setDocument((prev) => {
      const next = cloneDocument(prev);
      for (const id of OUTDOOR_AREA_IDS) {
        const list = next.outdoor[id].animals ?? [];
        const idx = list.findIndex((a) => a.id === animalId);
        if (idx >= 0) {
          const cur = list[idx]!;
          list[idx] = {
            ...cur,
            note: note === '' ? undefined : note,
          };
          next.outdoor[id].animals = list;
          break;
        }
      }
      return next;
    });
    markDirty();
  };

  const updateAreaWarps = (target: OutdoorAreaId, warps: VillageMapEdgeWarp[]) => {
    setDocument((prev) => {
      const next = cloneDocument(prev);
      next.outdoor[target].edgeWarps = warps;
      return next;
    });
    markDirty();
  };

  const pickMoveAt = (x: number, y: number) => {
    const rows =
      tab.mode === 'interior'
        ? documentRef.current.interiors[tab.interiorId]?.rows
        : documentRef.current.outdoor[tab.areaId]?.rows;
    if (!rows) return;
    const cells = pickMovableGroup(rows, x, y, tab.mode === 'interior');
    if (!cells?.length) return;
    setMoveSelection({ cells, grabX: x, grabY: y });
  };

  const placeMoveAt = (targetX: number, targetY: number) => {
    if (!moveSelection) return;
    const dx = targetX - moveSelection.grabX;
    const dy = targetY - moveSelection.grabY;
    if (dx === 0 && dy === 0) {
      setMoveSelection(null);
      return;
    }

    if (tab.mode === 'interior') {
      const interiorId = tab.interiorId;
      const room = documentRef.current.interiors[interiorId];
      if (!room) return;
      if (
        !moveGroupFits(
          room.rows,
          moveSelection.cells,
          moveSelection.grabX,
          moveSelection.grabY,
          targetX,
          targetY,
          true,
        )
      ) {
        return;
      }
      pushInteriorUndo(interiorId, room);
      setDocument((prev) => {
        const next = cloneDocument(prev);
        const nextRoom = next.interiors[interiorId];
        nextRoom.rows = applyMoveGroup(
          nextRoom.rows,
          moveSelection.cells,
          dx,
          dy,
          true,
        );
        nextRoom.furnitureWords = moveFurnitureWordsForGroup(
          nextRoom.furnitureWords,
          moveSelection.cells,
          dx,
          dy,
        );
        return next;
      });
      setMoveSelection(null);
      markDirty();
      return;
    }

    const areaId = tab.areaId;
    const area = documentRef.current.outdoor[areaId];
    if (
      !moveGroupFits(
        area.rows,
        moveSelection.cells,
        moveSelection.grabX,
        moveSelection.grabY,
        targetX,
        targetY,
        false,
      )
    ) {
      return;
    }
    pushUndo(areaId, cloneAreaRows(area), area.buildings, area.signs);
    setDocument((prev) => {
      const next = cloneDocument(prev);
      const outdoor = next.outdoor[areaId];
      outdoor.rows = applyMoveGroup(
        outdoor.rows,
        moveSelection.cells,
        dx,
        dy,
        false,
      );
      // Tips / lock messages / signs ride with ? / ` / ! cells when those move.
      const tips = { ...outdoor.tips };
      const lockMessages = { ...(outdoor.lockMessages ?? {}) };
      let signs = [...(outdoor.signs ?? [])];
      for (const cell of moveSelection.cells) {
        if (cell.ch === '?') {
          const from = tipKey(cell.x, cell.y);
          const text = tips[from];
          if (text != null) {
            delete tips[from];
            tips[tipKey(cell.x + dx, cell.y + dy)] = text;
          }
        }
        if (cell.ch === '`') {
          const from = tipKey(cell.x, cell.y);
          const text = lockMessages[from];
          if (text != null) {
            delete lockMessages[from];
            lockMessages[tipKey(cell.x + dx, cell.y + dy)] = text;
          }
        }
        if (cell.ch === '!') {
          const idx = signs.findIndex((s) => s.x === cell.x && s.y === cell.y);
          if (idx >= 0) {
            const sign = signs[idx]!;
            signs = [
              ...signs.slice(0, idx),
              ...signs.slice(idx + 1),
              { ...sign, x: cell.x + dx, y: cell.y + dy },
            ];
          }
        }
      }
      outdoor.tips = tips;
      outdoor.lockMessages = lockMessages;
      outdoor.signs = signs;
      return next;
    });
    setMoveSelection(null);
    markDirty();
  };

  const paintStroke = (cells: Array<{ x: number; y: number }>, ch: string) => {
    if (tab.mode === 'interior') {
      const interiorId = tab.interiorId;
      const room = documentRef.current.interiors[interiorId];
      if (!room) return;
      let rows = room.rows.slice();
      let changed = false;
      for (const cell of cells) {
        const prevCh = rows[cell.y]?.[cell.x];
        if (prevCh === ch) continue;
        rows = setCellChar(rows, cell.x, cell.y, ch);
        changed = true;
      }
      if (!changed) return;
      pushInteriorUndo(interiorId, room);
      setDocument((prev) => {
        const next = cloneDocument(prev);
        const nextRoom = next.interiors[interiorId];
        nextRoom.rows = rows;
        nextRoom.furnitureWords = pruneFurnitureWords(
          { ...nextRoom, rows },
          cells,
        );
        return next;
      });
      markDirty();
      return;
    }

    const areaId = tab.areaId;
    const area = documentRef.current.outdoor[areaId];
    let rows = cloneAreaRows(area);
    const tips = { ...area.tips };
    const lockMessages = { ...(area.lockMessages ?? {}) };
    let signs = [...(area.signs ?? [])];
    let tipsChanged = false;
    let lockMessagesChanged = false;
    let signsChanged = false;
    let changed = false;
    for (const cell of cells) {
      const prevCh = rows[cell.y]?.[cell.x];
      if (prevCh === ch) continue;
      rows = setCellChar(rows, cell.x, cell.y, ch);
      changed = true;
      if (prevCh === '?' && ch !== '?') {
        const key = tipKey(cell.x, cell.y);
        if (key in tips) {
          delete tips[key];
          tipsChanged = true;
        }
      }
      if (prevCh === '`' && ch !== '`') {
        const key = tipKey(cell.x, cell.y);
        if (key in lockMessages) {
          delete lockMessages[key];
          lockMessagesChanged = true;
        }
      }
      if (prevCh === '!' && ch !== '!') {
        const nextSigns = removeSignAt(signs, cell.x, cell.y);
        if (nextSigns.length !== signs.length) {
          signs = nextSigns;
          signsChanged = true;
        }
      }
      if (ch === '!' && prevCh !== '!') {
        signs = upsertSignAt(signs, cell.x, cell.y);
        signsChanged = true;
      }
    }
    if (!changed) return;
    const buildings = pruneBuildingsAfterPaint(
      area.buildings as PlacedBuilding[] | undefined,
      rows,
      cells,
    ) as VillageMapBuilding[] | undefined;
    const buildingsChanged =
      JSON.stringify(buildings ?? null) !== JSON.stringify(area.buildings ?? null);
    pushUndo(areaId, cloneAreaRows(area), area.buildings, area.signs);
    setDocument((prev) => {
      const next = cloneDocument(prev);
      next.outdoor[areaId].rows = rows;
      if (tipsChanged) next.outdoor[areaId].tips = tips;
      if (lockMessagesChanged) next.outdoor[areaId].lockMessages = lockMessages;
      if (buildingsChanged) next.outdoor[areaId].buildings = buildings;
      if (signsChanged) next.outdoor[areaId].signs = signs;
      return next;
    });
    markDirty();
  };

  const rectFill = (
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    ch: string,
  ) => {
    const minX = Math.min(x0, x1);
    const maxX = Math.max(x0, x1);
    const minY = Math.min(y0, y1);
    const maxY = Math.max(y0, y1);
    const cells: Array<{ x: number; y: number }> = [];
    for (let y = minY; y <= maxY; y += 1) {
      for (let x = minX; x <= maxX; x += 1) {
        cells.push({ x, y });
      }
    }
    paintStroke(cells, ch);
  };

  const placeStamp = (stamp: MapStamp, originX: number, originY: number) => {
    if (tab.mode !== 'outdoor') return;
    const areaId = tab.areaId;
    const area = documentRef.current.outdoor[areaId];
    let rows = cloneAreaRows(area);
    const tips = { ...area.tips };
    const lockMessages = { ...(area.lockMessages ?? {}) };
    let signs = [...(area.signs ?? [])];
    let tipsChanged = false;
    let lockMessagesChanged = false;
    let signsChanged = false;
    let changed = false;
    const writeCells = stampWriteCells(stamp, originX, originY);
    for (const cell of writeCells) {
      const prevCh = rows[cell.y]?.[cell.x];
      if (prevCh === cell.ch) continue;
      rows = setCellChar(rows, cell.x, cell.y, cell.ch);
      changed = true;
      if (prevCh === '?' && cell.ch !== '?') {
        const key = tipKey(cell.x, cell.y);
        if (key in tips) {
          delete tips[key];
          tipsChanged = true;
        }
      }
      if (prevCh === '`' && cell.ch !== '`') {
        const key = tipKey(cell.x, cell.y);
        if (key in lockMessages) {
          delete lockMessages[key];
          lockMessagesChanged = true;
        }
      }
      if (prevCh === '!' && cell.ch !== '!') {
        const nextSigns = removeSignAt(signs, cell.x, cell.y);
        if (nextSigns.length !== signs.length) {
          signs = nextSigns;
          signsChanged = true;
        }
      }
      if (cell.ch === '!' && prevCh !== '!') {
        signs = upsertSignAt(signs, cell.x, cell.y);
        signsChanged = true;
      }
    }
    const placed = buildingFromStamp(stamp, originX, originY);
    let buildings = pruneBuildingsAfterPaint(
      area.buildings as PlacedBuilding[] | undefined,
      rows,
      writeCells,
    ) as VillageMapBuilding[] | undefined;
    if (placed) {
      buildings = upsertBuildingPlacement(
        buildings as PlacedBuilding[] | undefined,
        placed,
        stamp,
      ) as VillageMapBuilding[];
    }
    const buildingsChanged =
      JSON.stringify(buildings ?? null) !== JSON.stringify(area.buildings ?? null);
    // Re-stamping the same house still refreshes ownership metadata.
    if (!changed && !buildingsChanged) return;
    pushUndo(areaId, cloneAreaRows(area), area.buildings, area.signs);
    setDocument((prev) => {
      const next = cloneDocument(prev);
      next.outdoor[areaId].rows = rows;
      if (tipsChanged) next.outdoor[areaId].tips = tips;
      if (lockMessagesChanged) next.outdoor[areaId].lockMessages = lockMessages;
      if (buildingsChanged) next.outdoor[areaId].buildings = buildings;
      if (signsChanged) next.outdoor[areaId].signs = signs;
      return next;
    });
    markDirty();
  };

  const undo = () => {
    const entry = undoStack.current.pop();
    if (!entry) return;
    if (entry.kind === 'interior') {
      setDocument((prev) => {
        const cur = prev.interiors[entry.interiorId];
        redoStack.current.push({
          kind: 'interior',
          interiorId: entry.interiorId,
          room: structuredClone(cur),
        });
        const next = cloneDocument(prev);
        next.interiors[entry.interiorId] = structuredClone(entry.room);
        return next;
      });
      syncUndoButtons();
      markDirty();
      return;
    }
    setDocument((prev) => {
      const cur = prev.outdoor[entry.areaId];
      redoStack.current.push({
        kind: 'outdoor',
        areaId: entry.areaId,
        rows: cloneAreaRows(cur),
        buildings: cloneBuildings(cur.buildings) ?? [],
        signs: cloneSigns(cur.signs) ?? [],
        npcsByArea: cloneNpcsByArea(prev),
        animalsByArea: cloneAnimalsByArea(prev),
      });
      const next = cloneDocument(prev);
      next.outdoor[entry.areaId].rows = entry.rows;
      next.outdoor[entry.areaId].buildings = entry.buildings ?? [];
      next.outdoor[entry.areaId].signs = entry.signs ?? [];
      applyNpcsByArea(next, entry.npcsByArea);
      applyAnimalsByArea(next, entry.animalsByArea);
      return next;
    });
    syncUndoButtons();
    markDirty();
  };

  const redo = () => {
    const entry = redoStack.current.pop();
    if (!entry) return;
    if (entry.kind === 'interior') {
      setDocument((prev) => {
        const cur = prev.interiors[entry.interiorId];
        undoStack.current.push({
          kind: 'interior',
          interiorId: entry.interiorId,
          room: structuredClone(cur),
        });
        const next = cloneDocument(prev);
        next.interiors[entry.interiorId] = structuredClone(entry.room);
        return next;
      });
      syncUndoButtons();
      markDirty();
      return;
    }
    setDocument((prev) => {
      const cur = prev.outdoor[entry.areaId];
      undoStack.current.push({
        kind: 'outdoor',
        areaId: entry.areaId,
        rows: cloneAreaRows(cur),
        buildings: cloneBuildings(cur.buildings) ?? [],
        signs: cloneSigns(cur.signs) ?? [],
        npcsByArea: cloneNpcsByArea(prev),
        animalsByArea: cloneAnimalsByArea(prev),
      });
      const next = cloneDocument(prev);
      next.outdoor[entry.areaId].rows = entry.rows;
      next.outdoor[entry.areaId].buildings = entry.buildings ?? [];
      next.outdoor[entry.areaId].signs = entry.signs ?? [];
      applyNpcsByArea(next, entry.npcsByArea);
      applyAnimalsByArea(next, entry.animalsByArea);
      return next;
    });
    syncUndoButtons();
    markDirty();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (mod || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key === 'p') {
        e.preventDefault();
        setTool('paint');
        setSelectedStampId(null);
        setMoveSelection(null);
      } else if (key === 'e') {
        e.preventDefault();
        setTool('eraser');
        setSelectedStampId(null);
        setMoveSelection(null);
      } else if (key === 'm') {
        e.preventDefault();
        setTool('move');
        setSelectedStampId(null);
      } else if (e.key === 'Escape' && moveSelection) {
        e.preventDefault();
        setMoveSelection(null);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setBrushSize((prev) => {
          const i = BRUSH_SIZES.indexOf(prev);
          return BRUSH_SIZES[Math.min(BRUSH_SIZES.length - 1, i + 1)] ?? prev;
        });
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setBrushSize((prev) => {
          const i = BRUSH_SIZES.indexOf(prev);
          return BRUSH_SIZES[Math.max(0, i - 1)] ?? prev;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const runSaveRef = useRef<(clearUndo: boolean) => void>(() => {});

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payloadDoc = documentRef.current;
      const payloadName = nameRef.current;
      const payloadNotes = notesRef.current;
      const payloadExpected = expectedUpdatedAtRef.current;
      const epochAtStart = editEpochRef.current;
      const validation = validateVillageMapDocument(payloadDoc);
      if (validation.errors.length) {
        setClientErrors(validation.errors);
        throw new Error(validation.errors.join('; '));
      }
      setClientErrors([]);
      const next = await apiJson<VillageMapRecord>(
        `/v1/admin/village-maps/${record.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            name: payloadName.trim() || 'Untitled village maps',
            notes: payloadNotes.trim() || null,
            document: payloadDoc,
            expected_updated_at: payloadExpected,
          }),
        },
      );
      return { next, epochAtStart };
    },
    onMutate: () => {
      savingRef.current = true;
    },
    onSuccess: ({ next, epochAtStart }) => {
      setExpectedUpdatedAt(next.updated_at);
      expectedUpdatedAtRef.current = next.updated_at;
      setServerWarnings(next.warnings ?? []);
      // Keep local edits that landed while the request was in flight.
      if (editEpochRef.current === epochAtStart) {
        setDocument(cloneDocument(next.document));
        setName(next.name);
        setNotes(next.notes ?? '');
        setDirty(false);
        dirtyRef.current = false;
        if (clearUndoOnSuccessRef.current) {
          undoStack.current = [];
          redoStack.current = [];
          setCanUndo(false);
          setCanRedo(false);
        }
        clearUndoOnSuccessRef.current = false;
        setSaveMessage('Saved');
      }
      // If epoch diverged, leave clearUndoOnSuccessRef for a coalesced follow-up.
      onSaved(next);
    },
    onError: (err) => {
      if (!pendingAutoSaveRef.current) {
        clearUndoOnSuccessRef.current = false;
      }
      const parsed = parseVillageMapSaveError(err);
      if (parsed.errors.length) setClientErrors(parsed.errors);
      setSaveMessage(parsed.message);
    },
    onSettled: () => {
      savingRef.current = false;
      if (!pendingAutoSaveRef.current) return;
      pendingAutoSaveRef.current = false;
      if (dirtyRef.current && (autoSaveRef.current || clearUndoOnSuccessRef.current)) {
        // Coalesce: one follow-up PATCH with the latest refs after in-flight save.
        const clearUndo = clearUndoOnSuccessRef.current;
        queueMicrotask(() => runSaveRef.current(clearUndo));
      } else {
        clearUndoOnSuccessRef.current = false;
      }
    },
  });

  runSaveRef.current = (clearUndo: boolean) => {
    if (savingRef.current || saveMutation.isPending) {
      pendingAutoSaveRef.current = true;
      if (clearUndo) clearUndoOnSuccessRef.current = true;
      return;
    }
    clearUndoOnSuccessRef.current = clearUndo;
    saveMutation.mutate();
  };

  // Auto-save: debounce edits, coalesce to one PATCH while a save is in flight.
  useEffect(() => {
    if (!autoSave || !dirty) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;
      if (!autoSaveRef.current || !dirtyRef.current) return;
      runSaveRef.current(false);
    }, AUTO_SAVE_DEBOUNCE_MS);
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [autoSave, dirty, document, name, notes]);

  const isInterior = tab.mode === 'interior';
  const cellSize = isInterior ? interiorCellSize : outdoorCellSize;
  const onCellSizeChange = isInterior
    ? onInteriorCellSizeChange
    : onOutdoorCellSizeChange;
  const areaId = tab.mode === 'outdoor' ? tab.areaId : 'town';
  const interiorId = tab.mode === 'interior' ? tab.interiorId : null;
  const outdoorArea = document.outdoor[areaId];
  const interiorRoom =
    interiorId != null ? document.interiors[interiorId] : null;
  const area = isInterior && interiorRoom
    ? interiorToGridArea(interiorRoom)
    : outdoorArea;
  const outdoorLegendEntries = legend.legend?.length
    ? legend.legend
    : LOCAL_LEGEND;
  const interiorLegendEntries = legend.interiorLegend?.length
    ? legend.interiorLegend
    : LOCAL_INTERIOR_LEGEND;
  const legendEntries = isInterior ? interiorLegendEntries : outdoorLegendEntries;
  const selectedCharLabel =
    legendEntries.find((e) => e.char === selectedChar)?.label ?? selectedChar;
  const selectedStamp = selectedStampId
    ? MAP_STAMP_BY_ID[selectedStampId]
    : null;

  // Inspect-mode mouse chip: tile under cursor (+ NPC/warp/etc. extras).
  const inspectHoverChar =
    hoverCell != null
      ? (area.rows[hoverCell.y]?.[hoverCell.x] ?? null)
      : null;
  const inspectHoverLabel =
    inspectHoverChar != null
      ? (legendEntries.find((e) => e.char === inspectHoverChar)?.label ??
        inspectHoverChar)
      : '';
  const inspectHoverReadout =
    hoverCell == null
      ? null
      : isInterior && interiorRoom
        ? formatInteriorHoverReadout({
            room: interiorRoom,
            x: hoverCell.x,
            y: hoverCell.y,
            legend: legendEntries,
          })
        : formatMapHoverReadout({
            area: outdoorArea,
            areaId,
            document,
            x: hoverCell.x,
            y: hoverCell.y,
            legend: legendEntries,
          });
  // Drop "x, y · Label (ch)" — chip already shows those; keep NPC/warp/…
  const inspectHoverDetail =
    inspectHoverReadout && inspectHoverReadout !== 'Off map'
      ? inspectHoverReadout.split(' · ').slice(2).join(' · ') || null
      : null;

  const selectedOutdoorBuilding =
    !isInterior && selectedCell
      ? outdoorArea.buildings?.find((b) => {
          if (b.door.x === selectedCell.x && b.door.y === selectedCell.y) {
            return true;
          }
          const stamp = MAP_STAMP_BY_ID[b.stampId];
          if (!stamp) return false;
          return stampWriteCells(stamp, b.origin.x, b.origin.y).some(
            (c) => c.x === selectedCell.x && c.y === selectedCell.y,
          );
        })
      : undefined;
  const editInteriorTarget =
    selectedOutdoorBuilding?.doorTarget &&
    isBuildingInteriorId(selectedOutdoorBuilding.doorTarget)
      ? selectedOutdoorBuilding.doorTarget
      : selectedOutdoorBuilding?.buildingId &&
          isBuildingInteriorId(selectedOutdoorBuilding.buildingId)
        ? selectedOutdoorBuilding.buildingId
        : null;

  const setInteriorKitField = (
    field: 'kit' | 'floorKit' | 'rugKit',
    kit: InteriorKit,
  ) => {
    if (!interiorId || !interiorRoom) return;
    const current =
      field === 'kit'
        ? interiorRoom.kit
        : field === 'floorKit'
          ? interiorFloorKitOf(interiorRoom)
          : interiorRugKitOf(interiorRoom);
    if (current === kit) return;
    pushInteriorUndo(interiorId, interiorRoom);
    setDocument((prev) => {
      const next = cloneDocument(prev);
      next.interiors[interiorId][field] = kit;
      return next;
    });
    markDirty();
  };

  const setInteriorKit = (kit: InteriorKit) => setInteriorKitField('kit', kit);

  const setInteriorSpawnOrExit = (
    field: 'spawn' | 'exitCell',
    x: number,
    y: number,
  ) => {
    if (!interiorId || !interiorRoom) return;
    const cur = interiorRoom[field];
    if (cur.x === x && cur.y === y) return;
    pushInteriorUndo(interiorId, interiorRoom);
    setDocument((prev) => {
      const next = cloneDocument(prev);
      next.interiors[interiorId][field] = { x, y };
      return next;
    });
    markDirty();
  };

  return (
    <>
      {/* Outside space-y: chip mounts on mousemove and would add a 1rem gap. */}
      <FloatingToolCursor
        tool={tool}
        selectedChar={selectedChar}
        selectedCharLabel={selectedCharLabel}
        selectedStampId={selectedStampId}
        showSprites={showSprites}
        brushSize={brushSize}
        interiorMode={isInterior}
        interiorKit={interiorRoom?.kit ?? 'gold'}
        interiorFloorKit={
          interiorRoom ? interiorFloorKitOf(interiorRoom) : 'gold'
        }
        interiorRugKit={interiorRoom ? interiorRugKitOf(interiorRoom) : 'gold'}
        inspectChar={tool === 'inspect' ? inspectHoverChar : null}
        inspectLabel={inspectHoverLabel}
        inspectDetail={inspectHoverDetail}
      />
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <Button variant="secondary" size="sm" onClick={onBack}>
            ← All village maps
          </Button>
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              markDirty();
            }}
            className="max-w-md text-lg font-semibold"
            aria-label="Map name"
          />
          <Input
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              markDirty();
            }}
            placeholder="Notes (optional)"
            className="max-w-xl text-sm"
            aria-label="Notes"
          />
          <p className="text-xs text-gray-500">
            Last saved {formatAdminDateTime(expectedUpdatedAt)}
            {dirty ? ' · unsaved changes' : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!canUndo}
            onClick={undo}
          >
            Undo
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!canRedo}
            onClick={redo}
          >
            Redo
          </Button>
          <Button
            type="button"
            size="sm"
            loading={saveMutation.isPending}
            disabled={!dirty || saveMutation.isPending}
            onClick={() => {
              if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
                autoSaveTimerRef.current = null;
              }
              setSaveMessage(null);
              runSaveRef.current(true);
            }}
          >
            Save
          </Button>
          <a
            href={`/v1/admin/village-maps/${record.id}/export`}
            download
            className="inline-flex items-center justify-center rounded-2xl bg-primary/10 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/20"
          >
            Export
          </a>
        </div>
      </div>

      {clientErrors.length > 0 ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <p className="font-medium">Fix before saving</p>
          <ul className="mt-1 list-disc pl-5">
            {clientErrors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {serverWarnings.length > 0 ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          <div className="flex items-start justify-between gap-2">
            <p className="font-medium">Warnings</p>
            <button
              type="button"
              className="text-xs text-amber-800 hover:underline"
              onClick={() => setServerWarnings([])}
            >
              Dismiss
            </button>
          </div>
          <ul className="mt-1 list-disc pl-5">
            {serverWarnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {(saveMutation.isPending || saveMessage) && !clientErrors.length ? (
        <p
          className={`text-sm ${
            saveMutation.isPending
              ? 'text-gray-500'
              : saveMutation.isError
                ? 'text-red-600'
                : 'text-green-700'
          }`}
        >
          {saveMutation.isPending ? 'Saving…' : saveMessage}
        </p>
      ) : null}

      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {OUTDOOR_AREA_IDS.map((id) => (
            <Button
              key={id}
              type="button"
              size="sm"
              variant={
                tab.mode === 'outdoor' && tab.areaId === id
                  ? 'primary'
                  : 'secondary'
              }
              onClick={() => switchToOutdoor(id)}
            >
              {AREA_TAB_LABEL[id]} ({AREA_DIMENSIONS[id].width}×
              {AREA_DIMENSIONS[id].height})
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Interiors
          </span>
          {BUILDING_INTERIOR_IDS.map((id) => (
            <Button
              key={id}
              type="button"
              size="sm"
              variant={
                tab.mode === 'interior' && tab.interiorId === id
                  ? 'primary'
                  : 'secondary'
              }
              onClick={() => switchToInterior(id)}
            >
              {INTERIOR_TAB_LABEL[id]} ({INTERIOR_WIDTH}×{INTERIOR_HEIGHT})
            </Button>
          ))}
        </div>
      </div>

      <Card className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {(
            (
              isInterior
                ? ([
                    ['paint', 'Paint', IconPaintBrush, 'P'],
                    ['rect', 'Rect fill', IconSquare, null],
                    ['move', 'Move', IconArrowsMove, 'M'],
                    ['inspect', 'Inspect', IconMagnifyingGlass, null],
                    ['npc', 'Resident', IconUsers, null],
                    ['eraser', 'Eraser', IconEraser, 'E'],
                  ] as const)
                : ([
                    ['paint', 'Paint', IconPaintBrush, 'P'],
                    ['rect', 'Rect fill', IconSquare, null],
                    ['move', 'Move', IconArrowsMove, 'M'],
                    ['inspect', 'Inspect', IconMagnifyingGlass, null],
                    ['npc', 'Place NPC', IconUsers, null],
                    ['animal', 'Place Animal', IconPaw, null],
                    ['eraser', 'Eraser', IconEraser, 'E'],
                  ] as const)
            )
          ).map(([id, label, Icon, hotkey]) => (
            <Button
              key={id}
              type="button"
              size="sm"
              variant={tool === id ? 'primary' : 'secondary'}
              className="gap-1.5"
              title={
                id === 'move'
                  ? 'Move furniture / props (M) — click to pick up, click to place, Esc to cancel'
                  : hotkey
                    ? `${label} (${hotkey})`
                    : label
              }
              onClick={() => {
                setTool(id);
                setSelectedStampId(null);
                if (id !== 'move') setMoveSelection(null);
              }}
            >
              <Icon size={14} />
              {label}
              {hotkey ? (
                <kbd className="ml-0.5 rounded border border-current/20 px-1 text-[10px] font-medium opacity-70">
                  {hotkey}
                </kbd>
              ) : null}
            </Button>
          ))}
          {isInterior && interiorRoom ? (
            <label className="ml-2 flex items-center gap-1.5 text-sm text-gray-600">
              <span>Walls</span>
              <select
                className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
                value={interiorRoom.kit}
                onChange={(e) =>
                  setInteriorKit(e.target.value as InteriorKit)
                }
                aria-label="Interior wall kit"
              >
                {INTERIOR_KITS.map((kit) => (
                  <option key={kit} value={kit}>
                    {kit}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {tool === 'paint' || tool === 'eraser' ? (
            <div className="ml-2 flex items-center gap-1.5 text-sm text-gray-600">
              <span>Brush</span>
              {BRUSH_SIZES.map((size) => (
                <Button
                  key={size}
                  type="button"
                  size="sm"
                  variant={brushSize === size ? 'primary' : 'secondary'}
                  onClick={() => setBrushSize(size)}
                  aria-label={`Brush size ${size} by ${size}`}
                  title={`${size}×${size} tile brush`}
                >
                  {size}×{size}
                </Button>
              ))}
            </div>
          ) : null}
          <div className="ml-2 flex items-center gap-1.5 text-sm text-gray-600">
            <span>Zoom</span>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={cellSize === ZOOM_OPTIONS[0]}
              onClick={() => onCellSizeChange(stepZoom(cellSize, -1))}
              aria-label="Zoom out"
            >
              −
            </Button>
            <select
              className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
              value={cellSize}
              onChange={(e) =>
                onCellSizeChange(Number(e.target.value) as ZoomLevel)
              }
              aria-label="Zoom level"
            >
              {ZOOM_OPTIONS.map((z) => (
                <option key={z} value={z}>
                  {z}px
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={cellSize === ZOOM_OPTIONS[ZOOM_OPTIONS.length - 1]}
              onClick={() => onCellSizeChange(stepZoom(cellSize, 1))}
              aria-label="Zoom in"
            >
              +
            </Button>
          </div>
          <label className="ml-2 flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={showSprites}
              onChange={(e) => setShowSprites(e.target.checked)}
            />
            Game sprites
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={autoSave}
              onChange={(e) => setAutoSave(e.target.checked)}
            />
            Auto-save
          </label>
          <span className="text-xs text-gray-500">
            {tool === 'npc' && selectedNpcId ? (
              <>
                {isInterior ? 'Resident' : 'NPC'}:{' '}
                <span className="font-medium">
                  {OUTDOOR_NPC_BY_ID[selectedNpcId]?.name ?? selectedNpcId}
                </span>{' '}
                — click a cell to place/move
              </>
            ) : tool === 'npc' ? (
              <>
                {isInterior
                  ? 'Resident — click a placed pin to select, or pick from catalog'
                  : 'Place NPC — click a placed NPC to select, or pick from catalog'}
              </>
            ) : !isInterior && tool === 'animal' && selectedAnimalInstanceId ? (
              <>
                Move instance:{' '}
                <span className="font-medium font-mono">
                  {selectedAnimalInstanceId}
                </span>{' '}
                — click a cell
              </>
            ) : !isInterior && tool === 'animal' && selectedAnimalKind ? (
              <>
                Add{' '}
                <span className="font-medium">
                  {OUTDOOR_ANIMAL_BY_ID[selectedAnimalKind]?.name ??
                    selectedAnimalKind}
                </span>{' '}
                — click a cell
              </>
            ) : !isInterior && tool === 'animal' ? (
              <>
                Place Animal — click a placed animal to select, or pick a kind
                from the catalog
              </>
            ) : selectedStamp ? (
              <>
                Stamp: <span className="font-medium">{selectedStamp.label}</span>
              </>
            ) : (
              <>
                Tile: <span className="font-mono">{selectedChar}</span>{' '}
                {selectedCharLabel}
                {tool === 'paint' || tool === 'eraser'
                  ? ` · ${brushSize}×${brushSize}`
                  : null}
              </>
            )}
          </span>
        </div>

        <div
          className="grid gap-4 lg:grid-cols-[var(--vm-palette)_minmax(0,1fr)_var(--vm-inspector)]"
          style={
            {
              '--vm-palette': paletteCollapsed ? '3.25rem' : '220px',
              '--vm-inspector': inspectorCollapsed ? '3.25rem' : '260px',
            } as CSSProperties
          }
        >
          {paletteCollapsed ? (
            <div className="flex flex-col items-center rounded-xl border border-gray-200 bg-gray-50 py-2">
              <PanelCollapseButton
                direction="right"
                label="Expand palette"
                expanded={false}
                onClick={() => setPaletteCollapsed(false)}
              />
            </div>
          ) : (
            <div className="rounded-xl border border-gray-100 p-2">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-gray-500">Palette</span>
                <PanelCollapseButton
                  direction="left"
                  label="Collapse palette"
                  expanded={true}
                  onClick={() => setPaletteCollapsed(true)}
                />
              </div>
              <MapPalette
                legend={legendEntries}
                selectedChar={selectedChar}
                selectedStampId={selectedStampId}
                showSprites={showSprites}
                interiorMode={isInterior}
                interiorKit={interiorRoom?.kit ?? 'gold'}
                interiorFloorKit={
                  interiorRoom ? interiorFloorKitOf(interiorRoom) : 'gold'
                }
                interiorRugKit={
                  interiorRoom ? interiorRugKitOf(interiorRoom) : 'gold'
                }
                onSelectTile={(ch) => {
                  setSelectedChar(ch);
                  setSelectedStampId(null);
                  setMoveSelection(null);
                  setTool(
                    tool === 'inspect' ||
                      tool === 'stamp' ||
                      tool === 'npc' ||
                      tool === 'animal' ||
                      tool === 'move'
                      ? 'paint'
                      : tool,
                  );
                }}
                onSelectStamp={(stampId) => {
                  if (isInterior) return;
                  setSelectedStampId(stampId);
                  setMoveSelection(null);
                  setTool('stamp');
                }}
              />
            </div>
          )}

          <div className="min-w-0 space-y-1.5">
            <MapGrid
              area={area}
              areaId={areaId}
              document={document}
              cellSize={cellSize}
              tool={tool}
              selectedChar={selectedChar}
              selectedStampId={selectedStampId}
              selectedNpcId={selectedNpcId}
              selectedAnimalKind={isInterior ? null : selectedAnimalKind}
              selectedAnimalInstanceId={
                isInterior ? null : selectedAnimalInstanceId
              }
              focusPulseKey={focusPulseKey}
              brushSize={brushSize}
              selectedCell={selectedCell}
              highlightCell={highlightCell}
              showSprites={showSprites}
              interiorMode={isInterior}
              interiorKit={interiorRoom?.kit ?? 'gold'}
              interiorFloorKit={
                interiorRoom ? interiorFloorKitOf(interiorRoom) : 'gold'
              }
              interiorRugKit={
                interiorRoom ? interiorRugKitOf(interiorRoom) : 'gold'
              }
              spawn={interiorRoom?.spawn ?? null}
              exitCell={interiorRoom?.exitCell ?? null}
              onSelectCell={(cell) => {
                setSelectedCell(cell);
                setHighlightCell(cell);
              }}
              onHoverCell={setHoverCell}
              onPaintStroke={paintStroke}
              onRectFill={rectFill}
              onStampPlace={placeStamp}
              onNpcPlace={placeNpc}
              onAnimalPlace={placeAnimal}
              onNpcPick={(id) => {
                setSelectedNpcId(id);
                setSelectedAnimalKind(null);
                setSelectedAnimalInstanceId(null);
                pulseEntityFocus();
              }}
              onAnimalPick={(id) => {
                setSelectedAnimalInstanceId(id);
                setSelectedAnimalKind(null);
                setSelectedNpcId(null);
                pulseEntityFocus();
              }}
              moveSelection={tool === 'move' ? moveSelection : null}
              onMovePick={pickMoveAt}
              onMovePlace={placeMoveAt}
              onMoveCancel={() => setMoveSelection(null)}
            />
            <p
              className="px-1 text-center font-mono text-xs text-gray-600"
              aria-live="polite"
            >
              {tool === 'move' && moveSelection
                ? `Moving ${moveSelection.cells.length} tile${moveSelection.cells.length === 1 ? '' : 's'} — click to place · Esc to cancel`
                : tool === 'move'
                  ? 'Move — click furniture or a prop to pick it up'
                  : hoverCell
                    ? isInterior && interiorRoom
                      ? formatInteriorHoverReadout({
                          room: interiorRoom,
                          x: hoverCell.x,
                          y: hoverCell.y,
                          legend: legendEntries,
                        })
                      : formatMapHoverReadout({
                          area: outdoorArea,
                          areaId,
                          document,
                          x: hoverCell.x,
                          y: hoverCell.y,
                          legend: legendEntries,
                        })
                    : 'Off map'}
            </p>
          </div>

          {inspectorCollapsed ? (
            <div className="flex flex-col items-center rounded-xl border border-gray-200 bg-gray-50 py-2">
              <PanelCollapseButton
                direction="left"
                label="Expand inspector"
                expanded={false}
                onClick={() => setInspectorCollapsed(false)}
              />
            </div>
          ) : (
            <div className="space-y-6 rounded-xl border border-gray-100 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-2">
                  <h2 className="text-sm font-medium text-gray-900">Inspector</h2>
                  {selectedCell ? (
                    <p className="text-sm text-gray-700">
                      ({selectedCell.x},{selectedCell.y}){' '}
                      <span className="font-mono">
                        {area.rows[selectedCell.y]?.[selectedCell.x] ?? '?'}
                      </span>
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500">
                      Click a cell with Inspect.
                    </p>
                  )}
                </div>
                <PanelCollapseButton
                  direction="right"
                  label="Collapse inspector"
                  expanded={true}
                  onClick={() => setInspectorCollapsed(true)}
                />
              </div>

              {!isInterior && editInteriorTarget && selectedOutdoorBuilding ? (
                <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
                  <p className="text-xs text-gray-600">
                    {selectedOutdoorBuilding.displayName}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    className="mt-1.5"
                    onClick={() => switchToInterior(editInteriorTarget)}
                  >
                    Edit interior
                  </Button>
                </div>
              ) : null}

              {isInterior && interiorRoom && interiorId ? (
                <>
                  <InspectorSection sectionId="interiorMeta" title="Room">
                    <p className="text-xs text-gray-500">
                      {INTERIOR_TAB_LABEL[interiorId]} · {interiorRoom.kind} ·{' '}
                      {interiorRoom.width}×{interiorRoom.height}
                    </p>
                    <label className="mt-2 block text-xs text-gray-600">
                      Walls
                      <select
                        className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
                        value={interiorRoom.kit}
                        onChange={(e) =>
                          setInteriorKit(e.target.value as InteriorKit)
                        }
                      >
                        {INTERIOR_KITS.map((kit) => (
                          <option key={kit} value={kit}>
                            {kit}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="mt-2 block text-xs text-gray-600">
                      Floor
                      <select
                        className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
                        value={interiorFloorKitOf(interiorRoom)}
                        onChange={(e) =>
                          setInteriorKitField(
                            'floorKit',
                            e.target.value as InteriorKit,
                          )
                        }
                      >
                        {INTERIOR_KITS.map((kit) => (
                          <option key={kit} value={kit}>
                            {kit}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="mt-2 block text-xs text-gray-600">
                      Rug
                      <select
                        className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
                        value={interiorRugKitOf(interiorRoom)}
                        onChange={(e) =>
                          setInteriorKitField(
                            'rugKit',
                            e.target.value as InteriorKit,
                          )
                        }
                      >
                        {INTERIOR_KITS.map((kit) => (
                          <option key={kit} value={kit}>
                            {kit}
                          </option>
                        ))}
                      </select>
                    </label>
                  </InspectorSection>

                  <InspectorSection sectionId="interiorSpawn" title="Spawn / exit">
                    <p className="text-xs text-gray-500">
                      Spawn is where the player appears; exitCell must be an{' '}
                      <code className="font-mono">E</code> mat.
                    </p>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <p className="font-medium text-gray-700">
                          Spawn ({interiorRoom.spawn.x},{interiorRoom.spawn.y})
                        </p>
                        {selectedCell ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="mt-1"
                            onClick={() =>
                              setInteriorSpawnOrExit(
                                'spawn',
                                selectedCell.x,
                                selectedCell.y,
                              )
                            }
                          >
                            Set to selection
                          </Button>
                        ) : null}
                      </div>
                      <div>
                        <p className="font-medium text-gray-700">
                          Exit ({interiorRoom.exitCell.x},
                          {interiorRoom.exitCell.y})
                        </p>
                        {selectedCell ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="mt-1"
                            onClick={() =>
                              setInteriorSpawnOrExit(
                                'exitCell',
                                selectedCell.x,
                                selectedCell.y,
                              )
                            }
                          >
                            Set to selection
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </InspectorSection>

                  <NpcPanel
                    area={area}
                    npcsElsewhere={OUTDOOR_AREA_IDS.flatMap((id) =>
                      (document.outdoor[id].npcs ?? []).map((n) => ({
                        ...n,
                        areaId: id,
                      })),
                    )}
                    selected={selectedCell}
                    selectedNpcId={selectedNpcId}
                    title="Residents"
                    hideNotes
                    onSelectNpcId={(id) => {
                      setSelectedNpcId(id);
                      if (id) {
                        setSelectedAnimalKind(null);
                        setSelectedAnimalInstanceId(null);
                        setTool('npc');
                      }
                    }}
                    onJump={(x, y, toAreaId) => {
                      if (toAreaId) {
                        switchToOutdoor(toAreaId);
                      }
                      setSelectedCell({ x, y });
                      setHighlightCell({ x, y });
                      pulseEntityFocus();
                    }}
                    onRemove={removeNpc}
                    onChangeNote={() => {
                      /* interior residents have no designer notes */
                    }}
                  />

                  <FurnitureWordPanel
                    room={interiorRoom}
                    selected={selectedCell}
                    onJump={(x, y) => {
                      setSelectedCell({ x, y });
                      setHighlightCell({ x, y });
                      setTool('inspect');
                    }}
                    onChangeWord={(x, y, word) => {
                      setDocument((doc) =>
                        setFurnitureWordOnInterior(doc, interiorId, x, y, word),
                      );
                      markDirty();
                    }}
                  />
                </>
              ) : (
                <>
                  <InspectorSection sectionId="playerStart" title="Player start">
                    <label className="block text-xs text-gray-600">
                      Area
                      <select
                        className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
                        value={document.playerStart.areaId}
                        onChange={(e) => {
                          const nextArea = e.target.value as OutdoorAreaId;
                          const dims = AREA_DIMENSIONS[nextArea];
                          setDocument((prev) => ({
                            ...prev,
                            playerStart: {
                              areaId: nextArea,
                              x: Math.min(prev.playerStart.x, dims.width - 1),
                              y: Math.min(prev.playerStart.y, dims.height - 1),
                            },
                          }));
                          markDirty();
                        }}
                      >
                        {OUTDOOR_AREA_IDS.map((id) => (
                          <option key={id} value={id}>
                            {AREA_TAB_LABEL[id]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block text-xs text-gray-600">
                        x
                        <Input
                          type="number"
                          className="mt-0.5 !px-2 !py-1.5 text-sm"
                          value={document.playerStart.x}
                          onChange={(e) => {
                            const dims =
                              AREA_DIMENSIONS[document.playerStart.areaId];
                            const x = Math.max(
                              0,
                              Math.min(
                                dims.width - 1,
                                Number(e.target.value) || 0,
                              ),
                            );
                            setDocument((prev) => ({
                              ...prev,
                              playerStart: { ...prev.playerStart, x },
                            }));
                            markDirty();
                          }}
                        />
                      </label>
                      <label className="block text-xs text-gray-600">
                        y
                        <Input
                          type="number"
                          className="mt-0.5 !px-2 !py-1.5 text-sm"
                          value={document.playerStart.y}
                          onChange={(e) => {
                            const dims =
                              AREA_DIMENSIONS[document.playerStart.areaId];
                            const y = Math.max(
                              0,
                              Math.min(
                                dims.height - 1,
                                Number(e.target.value) || 0,
                              ),
                            );
                            setDocument((prev) => ({
                              ...prev,
                              playerStart: { ...prev.playerStart, y },
                            }));
                            markDirty();
                          }}
                        />
                      </label>
                    </div>
                  </InspectorSection>

                  <NpcPanel
                    area={outdoorArea}
                    npcsElsewhere={OUTDOOR_AREA_IDS.filter(
                      (id) => id !== areaId,
                    ).flatMap((id) =>
                      (document.outdoor[id].npcs ?? []).map((n) => ({
                        ...n,
                        areaId: id,
                      })),
                    )}
                    selected={selectedCell}
                    selectedNpcId={selectedNpcId}
                    onSelectNpcId={(id) => {
                      setSelectedNpcId(id);
                      if (id) {
                        setSelectedAnimalKind(null);
                        setSelectedAnimalInstanceId(null);
                        setTool('npc');
                      }
                    }}
                    onJump={(x, y, toAreaId) => {
                      if (toAreaId && toAreaId !== areaId) {
                        switchToOutdoor(toAreaId);
                      }
                      setSelectedCell({ x, y });
                      setHighlightCell({ x, y });
                      pulseEntityFocus();
                    }}
                    onRemove={removeNpc}
                    onChangeNote={updateNpcNote}
                  />

                  <AnimalPanel
                    area={outdoorArea}
                    animalsElsewhere={OUTDOOR_AREA_IDS.filter(
                      (id) => id !== areaId,
                    ).flatMap((id) =>
                      (document.outdoor[id].animals ?? []).map((a) => ({
                        ...a,
                        areaId: id,
                      })),
                    )}
                    selected={selectedCell}
                    selectedAnimalKind={selectedAnimalKind}
                    onSelectAnimalKind={(kind) => {
                      setSelectedAnimalKind(kind);
                      if (kind) {
                        setSelectedAnimalInstanceId(null);
                        setSelectedNpcId(null);
                        setTool('animal');
                      }
                    }}
                    selectedAnimalInstanceId={selectedAnimalInstanceId}
                    onSelectAnimalInstanceId={(id) => {
                      setSelectedAnimalInstanceId(id);
                      if (id) {
                        setSelectedAnimalKind(null);
                        setSelectedNpcId(null);
                        setTool('animal');
                      }
                    }}
                    onJump={(x, y, toAreaId) => {
                      if (toAreaId && toAreaId !== areaId) {
                        switchToOutdoor(toAreaId);
                      }
                      setSelectedCell({ x, y });
                      setHighlightCell({ x, y });
                      pulseEntityFocus();
                    }}
                    onRemove={removeAnimal}
                    onChangeNote={updateAnimalNote}
                  />

                  <SignPanel
                    area={outdoorArea}
                    selected={selectedCell}
                    onJump={(x, y) => {
                      setSelectedCell({ x, y });
                      setHighlightCell({ x, y });
                      setTool('inspect');
                    }}
                    onChangeSign={(x, y, text) => {
                      updateAreaSigns(
                        areaId,
                        upsertSignAt(outdoorArea.signs, x, y, text),
                      );
                    }}
                  />

                  <TipPanel
                    area={outdoorArea}
                    selected={selectedCell}
                    onJump={(x, y) => {
                      setSelectedCell({ x, y });
                      setHighlightCell({ x, y });
                      setTool('inspect');
                    }}
                    onChangeTip={(x, y, text) => {
                      const key = tipKey(x, y);
                      const tips = { ...outdoorArea.tips };
                      if (text === '') delete tips[key];
                      else tips[key] = text;
                      updateAreaTips(areaId, tips);
                    }}
                  />

                  <LockMessagePanel
                    area={outdoorArea}
                    selected={selectedCell}
                    onJump={(x, y) => {
                      setSelectedCell({ x, y });
                      setHighlightCell({ x, y });
                      setTool('inspect');
                    }}
                    onChangeLockMessage={(x, y, text) => {
                      const key = tipKey(x, y);
                      const lockMessages = {
                        ...(outdoorArea.lockMessages ?? {}),
                      };
                      if (text === '') delete lockMessages[key];
                      else lockMessages[key] = text;
                      updateAreaLockMessages(areaId, lockMessages);
                    }}
                  />

                  <WarpPanel
                    area={outdoorArea}
                    onChange={(warps) => updateAreaWarps(areaId, warps)}
                    onJump={(x, y) => {
                      setSelectedCell({ x, y });
                      setHighlightCell({ x, y });
                      setTool('inspect');
                    }}
                    quickAddFrom={selectedCell}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
    </>
  );
}
